import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { getAccountingChecklist } from "../../api/accounting";
import { useAuth } from "../../context/AuthContext";
import { isSalonBusinessCpaAccountant } from "../../utils/accountingRoles";
import {
  accountingContextFromSearchParams,
  accountingPath,
  hasAccountingContext,
} from "./accountingRouteContext";
import SalonBusinessCpaShell from "./SalonBusinessCpaShell";
import {
  getTaskActionPath,
  taskStatusClasses,
} from "./accountingTasks";

const fallbackSections = [
  {
    title: "Documents",
    body: "Gather the core records needed for accounting review.",
    items: ["Bank statements", "Payroll records", "GST/HST filings"],
  },
  {
    title: "Monthly review",
    body: "Keep sales, expenses, and deposits organized each month.",
    items: ["Review sales deposits", "Confirm expense categories", "Flag owner draws"],
  },
];

export default function SalonBusinessCpaChecklist() {
  const { token, user } = useAuth();
  const [searchParams] = useSearchParams();
  const accountingContext = useMemo(
    () => accountingContextFromSearchParams(searchParams),
    [searchParams]
  );
  const accountantView = isSalonBusinessCpaAccountant(user);
  const scopedAccountantView = accountantView && hasAccountingContext(accountingContext);
  const [sections, setSections] = useState(fallbackSections);
  const [loading, setLoading] = useState(true);

  const taskPath = (task) => {
    const rawPath = getTaskActionPath(task);
    const [path, query = ""] = rawPath.split("?");
    return accountingPath(path, accountingContext, Object.fromEntries(new URLSearchParams(query)));
  };

  useEffect(() => {
    if (!token) return;
    let active = true;
    setLoading(true);
    getAccountingChecklist(token, accountingContext)
      .then((data) => {
        if (!active) return;
        if (Array.isArray(data?.sections) && data.sections.length) {
          setSections(data.sections);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [accountingContext, token]);

  return (
    <SalonBusinessCpaShell
      title="Accounting checklist"
      description={
        scopedAccountantView
          ? "Review the checklist for this assigned Salon ID and see which records are still needed."
          : "A simple starter list for the records and review items that keep salon accounting cleaner."
      }
      actions={
        <Link
          to={accountingPath(accountantView ? "/review" : "/documents", accountingContext)}
          className="rounded-full bg-rose-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-rose-600"
        >
          {accountantView ? "Open review queue" : "Upload documents"}
        </Link>
      }
    >
      {loading ? (
        <div className="rounded-3xl border border-slate-200 bg-white p-6 text-sm text-slate-500">
          Loading checklist...
        </div>
      ) : (
        <section className="space-y-5">
          {sections.map((section) => {
            const tasks = Array.isArray(section.tasks) && section.tasks.length
              ? section.tasks
              : (section.items || []).map((item) => ({
                  code: `${section.title}-${item}`,
                  title: item,
                  body: section.body,
                  category: section.title,
                  status: "not_started",
                  status_label: "Not started",
                  action_label: "Review item",
                }));
            return (
              <article
                key={section.title}
                className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm"
              >
                <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-950">
                      {section.title}
                    </h2>
                    <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                      {section.body}
                    </p>
                  </div>
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                    {tasks.length} item{tasks.length === 1 ? "" : "s"}
                  </span>
                </div>

                <div className="mt-5 grid gap-3 lg:grid-cols-2">
                  {tasks.map((task) => (
                    <div
                      key={task.code || task.title}
                      className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                    >
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="text-sm font-semibold text-slate-950">
                              {task.title}
                            </h3>
                            {task.required === false ? (
                              <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-slate-500">
                                If needed
                              </span>
                            ) : null}
                            <span
                              className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${
                                taskStatusClasses[task.status] ||
                                "border-slate-200 bg-slate-100 text-slate-700"
                              }`}
                            >
                              {task.status_label || "Needed"}
                            </span>
                          </div>
                          <p className="mt-2 text-sm leading-6 text-slate-600">
                            {task.body}
                          </p>
                          {task.document_type_label ? (
                            <p className="mt-2 text-xs font-semibold text-slate-500">
                              Upload type: {task.document_type_label}
                            </p>
                          ) : null}
                        </div>
                        <Link
                          to={taskPath(task)}
                          className="inline-flex min-h-10 shrink-0 items-center justify-center rounded-full border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                        >
                          {accountantView ? "Review records" : task.action_label || "Review item"}
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </article>
            );
          })}
        </section>
      )}
    </SalonBusinessCpaShell>
  );
}
